import { useEffect, useRef, useState } from "react";

const STYLE_ID = "rm-central-animation-styles";

export const MOTION = {
  pageEnter: "page-enter",
  skeletonBlock: "skeleton-block",
  adminActionButton: "admin-action-button",
  productCard: "product-card",
  productQuickHost: "product-quick-host",
  productQuick: "product-quick",
  audioProgress: "audio-progress",
  audioThumb: "audio-thumb",
  shareContainer: "share-container",
  shareLink: "share-link",
};

export const REVEAL_ANIMATIONS = {
  slideInUp: "slideInUp",
  fadeInUp: "fadeInUp",
  fadeInRight: "fadeInRight",
  fadeInDown: "fadeInDown",
  slideInDown: "slideInDown",
  slideInRight: "slideInRight",
  zoomIn: "zoomIn",
};

const ANIMATION_ALIASES = {
  slideInLeft: "fadeInRight",
  zoomFade: "zoomIn",
};

const CENTRAL_ANIMATION_CSS = `
  html {
    scroll-behavior: smooth;
  }

  @keyframes rm-page-enter {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes rm-slide-in-up {
    from {
      opacity: 0;
      transform: translate3d(0, 20px, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  @keyframes rm-fade-in-up {
    from {
      opacity: 0;
      transform: translate3d(0, 14px, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  @keyframes rm-fade-in-right {
    from {
      opacity: 0;
      transform: translate3d(20px, 0, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  @keyframes rm-fade-in-down {
    from {
      opacity: 0;
      transform: translate3d(0, -14px, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  @keyframes rm-slide-in-right {
    from {
      opacity: 0;
      transform: translate3d(20px, 0, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  @keyframes rm-slide-in-down {
    from {
      opacity: 0;
      transform: translate3d(0, -20px, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  @keyframes rm-zoom-in {
    from {
      opacity: 0;
      transform: scale3d(0.94, 0.94, 0.94);
    }
    to {
      opacity: 1;
      transform: scale3d(1, 1, 1);
    }
  }

  @keyframes rm-shimmer {
    100% {
      transform: translateX(100%);
    }
  }

  [data-motion="${MOTION.pageEnter}"] {
    animation: rm-page-enter 260ms ease-out both;
  }

  .animate__animated {
    animation-duration: var(--motion-duration, 0.7s);
    animation-fill-mode: both;
    animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
    will-change: transform, opacity;
  }

  .animate__slideInUp {
    animation-name: rm-slide-in-up;
  }

  .animate__fadeInUp {
    animation-name: rm-fade-in-up;
  }

  .animate__fadeInRight {
    animation-name: rm-fade-in-right;
  }

  .animate__fadeInDown {
    animation-name: rm-fade-in-down;
  }

  .animate__slideInRight {
    animation-name: rm-slide-in-right;
  }

  .animate__slideInDown {
    animation-name: rm-slide-in-down;
  }

  .animate__zoomIn {
    animation-name: rm-zoom-in;
  }

  [data-motion="${MOTION.skeletonBlock}"]::after {
    transform: translateX(-100%);
    animation: rm-shimmer 1.2s ease-in-out infinite;
  }

  [data-motion="${MOTION.adminActionButton}"] {
    transition: transform 140ms ease, box-shadow 140ms ease, opacity 140ms ease;
  }

  [data-motion="${MOTION.adminActionButton}"]:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  [data-motion="${MOTION.productCard}"] {
    transition: box-shadow 0.2s ease, transform 0.2s ease;
  }

  [data-motion="${MOTION.productCard}"]:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, var(--color-shadowOpacity, 0.25));
    transform: translateY(-2px);
  }

  [data-motion="${MOTION.productQuick}"] {
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  [data-motion="${MOTION.productQuickHost}"]:hover [data-motion="${MOTION.productQuick}"] {
    opacity: 1;
  }

  [data-motion="${MOTION.audioProgress}"] {
    transition: width 0.15s ease-out;
  }

  input[data-motion="${MOTION.audioThumb}"]::-webkit-slider-thumb,
  input[data-motion="${MOTION.audioThumb}"]::-moz-range-thumb {
    transition: box-shadow 0.3s ease;
  }

  [data-motion="${MOTION.shareContainer}"] {
    animation-duration: 0.35s;
    animation-timing-function: ease-out;
  }

  [data-motion="${MOTION.shareLink}"] {
    animation-duration: 0.4s;
    animation-timing-function: ease-out;
  }

  .video-row .carousel-btn {
    opacity: 0;
    z-index: 6;
    scale: 2;
    transition: opacity 180ms ease, transform 180ms ease;
  }

  .video-row:hover .carousel-btn,
  .video-row:focus-within .carousel-btn {
    opacity: 1;
  }

  .video-row .carousel-btn--prev {
    transform: translate(-8px, -50%);
  }

  .video-row .carousel-btn--next {
    transform: translate(8px, -50%);
  }

  .video-row:hover .carousel-btn--prev,
  .video-row:focus-within .carousel-btn--prev {
    transform: translate(0, -50%);
  }

  .video-row:hover .carousel-btn--next,
  .video-row:focus-within .carousel-btn--next {
    transform: translate(0, -50%);
  }

  .video-item {
    transition: transform 220ms ease;
  }

  .video-item:hover {
    opacity: 1;
    transform: scale(1.025);
  }

  @media (prefers-reduced-motion: reduce) {
    html {
      scroll-behavior: auto;
    }

    [data-motion="${MOTION.pageEnter}"],
    .animate__animated,
    [data-motion="${MOTION.skeletonBlock}"]::after {
      animation: none !important;
    }

    [data-motion="${MOTION.adminActionButton}"],
    [data-motion="${MOTION.productCard}"],
    [data-motion="${MOTION.productQuick}"],
    [data-motion="${MOTION.audioProgress}"],
    input[data-motion="${MOTION.audioThumb}"],
    .video-row .carousel-btn,
    .video-item {
      transition: none !important;
    }

    .video-row .carousel-btn--prev,
    .video-row .carousel-btn--next,
    .video-item:hover,
    [data-motion="${MOTION.adminActionButton}"]:hover:not(:disabled),
    [data-motion="${MOTION.productCard}"]:hover {
      transform: none !important;
    }
  }
`;

export const ensureAnimationStyles = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CENTRAL_ANIMATION_CSS;
  document.head.appendChild(style);
};

const resolveAnimationName = (animation = "slideInUp") => {
  const resolved = ANIMATION_ALIASES[animation] || animation;
  return resolved.startsWith("animate__") ? resolved : `animate__${resolved}`;
};

export const getAnimatedClassName = (animation = "slideInUp") =>
  `animate__animated ${resolveAnimationName(animation)}`;

export const getAnimationDelayStyle = (delaySeconds = 0) => ({
  animationDelay: `${Math.max(0, Number(delaySeconds) || 0)}s`,
});

const animateRevealNode = (element, delaySeconds, options) => {
  if (!(element instanceof Element)) return;

  element.style.animationDelay = `${delaySeconds}s`;
  element.classList.add("animate__animated", resolveAnimationName(
    element.getAttribute("data-animation") || options.defaultAnimation,
  ));

  if (options.mediaSelector) {
    const media = element.querySelector(options.mediaSelector);
    if (media instanceof Element) {
      media.style.animationDelay = `${delaySeconds + options.mediaDelay}s`;
      media.classList.add(
        "animate__animated",
        resolveAnimationName(options.mediaAnimation),
      );
    }
  }

  if (options.bodySelector) {
    const body = element.querySelector(options.bodySelector);
    if (body instanceof Element) {
      Array.from(body.children).forEach((child, childIndex) => {
        if (!(child instanceof Element)) return;
        child.style.animationDelay = `${
          delaySeconds + options.childDelayStart + childIndex * options.childDelayStep
        }s`;
        child.classList.add(
          "animate__animated",
          resolveAnimationName(options.childAnimation),
        );
      });
    }
  }
};

export const useStaggeredReveal = (
  containerClassName,
  deps = [],
  {
    itemSelector = "[data-animation]",
    defaultAnimation = "slideInUp",
    mediaSelector = "",
    mediaAnimation = "fadeInUp",
    mediaDelay = 0.04,
    bodySelector = "",
    childAnimation = "slideInUp",
    childDelayStart = 0.08,
    childDelayStep = 0.06,
    threshold = 0.15,
  } = {},
) => {
  const depsKey = JSON.stringify(deps);

  useEffect(() => {
    ensureAnimationStyles();
    if (typeof window === "undefined") return undefined;

    const root = document.querySelector(`.${containerClassName}`);
    if (!(root instanceof Element)) return undefined;

    const elements = Array.from(root.querySelectorAll(itemSelector));
    if (!elements.length) return undefined;

    const options = {
      defaultAnimation,
      mediaSelector,
      mediaAnimation,
      mediaDelay,
      bodySelector,
      childAnimation,
      childDelayStart,
      childDelayStep,
    };

    if (typeof IntersectionObserver === "undefined") {
      elements.forEach((element) => {
        const index = Number(element.getAttribute("data-reveal-index") || 0);
        animateRevealNode(element, index * 0.12, options);
      });
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(
            entry.target.getAttribute("data-reveal-index") || 0,
          );
          animateRevealNode(entry.target, index * 0.12, options);
          observer.unobserve(entry.target);
        });
      },
      { threshold },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [containerClassName, itemSelector, defaultAnimation, mediaSelector, mediaAnimation, mediaDelay, bodySelector, childAnimation, childDelayStart, childDelayStep, threshold, depsKey]);
};

export const useArticleBodyReveal = (containerClassName, deps = []) => {
  const depsKey = JSON.stringify(deps);

  useEffect(() => {
    ensureAnimationStyles();
    if (typeof window === "undefined") return undefined;

    const root = document.querySelector(`.${containerClassName}`);
    if (!(root instanceof Element)) return undefined;

    Array.from(root.querySelectorAll("*")).forEach((element) => {
      if (!(element instanceof Element)) return;
      const tag = (element.tagName || "").toLowerCase();
      if (tag === "span" || element.getAttribute("data-animation")) return;
      element.setAttribute("data-animation", tag === "img" ? "zoomIn" : "fadeInRight");
    });

    const animatedEls = Array.from(root.querySelectorAll("[data-animation]"));
    if (!animatedEls.length) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      animatedEls.forEach((element) => {
        element.classList.add(
          "animate__animated",
          resolveAnimationName(element.getAttribute("data-animation") || "fadeInRight"),
        );
      });
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add(
            "animate__animated",
            resolveAnimationName(
              entry.target.getAttribute("data-animation") || "fadeInRight",
            ),
          );
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15 },
    );

    animatedEls.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [containerClassName, depsKey]);
};

export const useShareDockedMotion = (layout = "auto") => {
  const rootRef = useRef(null);
  const hasIntersectedRef = useRef(false);
  const [docked, setDocked] = useState(false);

  useEffect(() => {
    ensureAnimationStyles();
    if (layout !== "auto") {
      setDocked(layout === "vertical");
      return undefined;
    }

    const element = rootRef.current;
    if (!(element instanceof Element) || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          hasIntersectedRef.current = true;
          setDocked(false);
          return;
        }
        if (hasIntersectedRef.current) {
          setDocked(true);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -30% 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [layout]);

  const isDocked =
    layout === "vertical" ? true : layout === "horizontal" ? false : docked;

  return {
    rootRef,
    isDocked,
    containerAnimationClassName: getAnimatedClassName(
      isDocked ? "slideInRight" : "slideInDown",
    ),
    linkAnimationClassName: getAnimatedClassName(
      isDocked ? "fadeInRight" : "fadeInDown",
    ),
  };
};
